import React from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/common/PageTitle';
import { createStyle, isIOS, isWechat } from '../../utils/tools';

import tip from '../../assets/images/download/upright-tips.png';
import AppStoreIcon from '../../assets/images/download/app-store.png';
import GooglePlayIcon from '../../assets/images/download/google-play-badge.png';
import AndroidIcon from '../../assets/images/download/android-download.png';
import SupplyChainLogo from '../../assets/images/luniusupplychain/luniu-supply-chain.png';
import Screenshot1 from '../../assets/images/luniusupplychain/download/1.jpg';
import Screenshot2 from '../../assets/images/luniusupplychain/download/2.jpg';
import Screenshot3 from '../../assets/images/luniusupplychain/download/3.jpg';

const IOS_LINK = 'https://apps.apple.com/ca/app/id6448396737';
const GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=com.luniumall.grandintl';
const APK_LINK = 'https://download.luniumall.com/files/LuniuSupplyChain.apk';

const TEXT_DARK = '#1e3d20';
const TEXT_MUTED = '#557057';

const Download = () => {
  const { t } = useTranslation();

  const screenshots = [Screenshot1, Screenshot2, Screenshot3];

  const features = [
    {
      icon: '🥬',
      title: t('luniuSupplyChain.download.feature1Title'),
      desc: t('luniuSupplyChain.download.feature1Desc'),
    },
    {
      icon: '🚚',
      title: t('luniuSupplyChain.download.feature2Title'),
      desc: t('luniuSupplyChain.download.feature2Desc'),
    },
    {
      icon: '💰',
      title: t('luniuSupplyChain.download.feature3Title'),
      desc: t('luniuSupplyChain.download.feature3Desc'),
    },
  ];

  return (
    <div style={styles.root}>
      <PageTitle title={t('luniuSupplyChain.download.title')} />

      {isWechat() && (
        <div className="cover" style={styles.cover}>
          <div style={styles.tipContainer}>
            <img style={styles.tipImg} src={tip} alt="open browser tip" />
          </div>
        </div>
      )}

      <main style={styles.main}>
        <section style={styles.hero}>
          <img
            src={SupplyChainLogo}
            alt="Luniu Supply Chain"
            style={styles.logo}
          />
          <h1 style={styles.heroTagline}>
            {t('luniuSupplyChain.download.heroTagline')}
          </h1>
          <p style={styles.heroLine}>
            {t('luniuSupplyChain.download.heroLine1')}
          </p>
          <p style={styles.heroLine}>
            {t('luniuSupplyChain.download.heroLine2')}
          </p>

          <div style={styles.downloadButtons}>
            {isIOS() ? (
              <a href={IOS_LINK} style={styles.badgeLink}>
                <img
                  src={AppStoreIcon}
                  alt="Download on the App Store"
                  style={styles.badgeImg}
                />
              </a>
            ) : (
              <>
                <a href={GOOGLE_PLAY_LINK} style={styles.badgeLink}>
                  <img
                    src={GooglePlayIcon}
                    alt="Get it on Google Play"
                    style={styles.badgeImg}
                  />
                </a>
                <a href={APK_LINK} download style={styles.badgeLink}>
                  <img
                    src={AndroidIcon}
                    alt="Direct APK download"
                    style={styles.badgeImg}
                  />
                </a>
              </>
            )}
          </div>
        </section>

        <section style={styles.features}>
          <h2 style={styles.sectionTitle}>
            {t('luniuSupplyChain.download.featuresTitle')}
          </h2>
          <div style={styles.featureGrid}>
            {features.map((f) => (
              <div key={f.title} style={styles.featureCard}>
                <div style={styles.featureIcon}>{f.icon}</div>
                <div style={styles.featureTitle}>{f.title}</div>
                <div style={styles.featureDesc}>{f.desc}</div>
              </div>
            ))}
          </div>
        </section>

        <section style={styles.showcase}>
          <h2 style={styles.sectionTitle}>
            {t('luniuSupplyChain.download.screenshotsTitle')}
          </h2>
          <div style={styles.screenshotRow}>
            {screenshots.map((src, i) => (
              <div key={i} style={styles.screenshotWrap}>
                <img
                  src={src}
                  alt={`App screenshot ${i + 1}`}
                  style={styles.screenshot}
                />
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

const styles = createStyle({
  root: {
    minHeight: '100vh',
    background:
      'linear-gradient(180deg, #f3faea 0%, #fdf7eb 55%, #fffaf1 100%)',
    color: TEXT_DARK,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif',
  },
  cover: {
    position: 'fixed',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(0,0,0,0.8)',
    zIndex: 999,
    width: '100%',
    height: '100%',
  },
  tipContainer: {},
  tipImg: {
    maxWidth: '100%',
  },
  main: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '32px 20px 64px',
  },

  hero: {
    textAlign: 'center',
    padding: '24px 12px 8px',
  },
  logo: {
    width: '72%',
    maxWidth: 360,
    height: 'auto',
    objectFit: 'contain',
  },
  heroTagline: {
    margin: '20px 0 12px',
    fontSize: 26,
    fontWeight: 700,
    color: TEXT_DARK,
    letterSpacing: 1,
  },
  heroLine: {
    margin: '4px 0',
    fontSize: 15,
    color: TEXT_MUTED,
    lineHeight: 1.6,
  },
  downloadButtons: {
    marginTop: 28,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 14,
    justifyContent: 'center',
  },
  badgeLink: {
    display: 'inline-block',
    transition: 'transform 0.2s ease',
  },
  badgeImg: {
    height: 56,
    width: 'auto',
    maxWidth: '100%',
    display: 'block',
    borderRadius: 8,
    boxShadow: '0 6px 18px rgba(30, 61, 32, 0.12)',
  },

  features: {
    marginTop: 56,
  },
  sectionTitle: {
    margin: '0 0 24px',
    textAlign: 'center',
    fontSize: 22,
    fontWeight: 700,
    color: TEXT_DARK,
    position: 'relative',
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
  },
  featureCard: {
    background: '#ffffff',
    borderRadius: 16,
    padding: '24px 20px',
    textAlign: 'center',
    boxShadow: '0 8px 24px rgba(30, 61, 32, 0.06)',
    border: '1px solid rgba(44, 174, 53, 0.08)',
  },
  featureIcon: {
    fontSize: 36,
    marginBottom: 10,
  },
  featureTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: TEXT_DARK,
    marginBottom: 6,
  },
  featureDesc: {
    fontSize: 14,
    color: TEXT_MUTED,
    lineHeight: 1.6,
  },

  showcase: {
    marginTop: 56,
  },
  screenshotRow: {
    display: 'flex',
    gap: 16,
    overflowX: 'auto',
    padding: '8px 4px 16px',
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x mandatory',
    justifyContent: 'center',
  },
  screenshotWrap: {
    flex: '0 0 auto',
    width: 'min(78vw, 280px)',
    borderRadius: 22,
    overflow: 'hidden',
    boxShadow: '0 18px 40px rgba(30, 61, 32, 0.14)',
    scrollSnapAlign: 'center',
    background: '#fff',
  },
  screenshot: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
});

export default Download;
